import type { AllChartsDataMap, IPAChartDataPoint } from '../modules/analytic/types';
import type { IOrderListingLatestDataContext } from '~/modules/order-analytic';
import type { DataSeries } from '@shopify/polaris-viz';
import type { DateTimeFilter } from '~/presentation/ui';
import type { IOrder } from '@gem-sdk/analytics';
import type { Dayjs } from '~/utils/dayjs';

import {
  DEMO_DAILY_SERIES,
  DEMO_DATE_FILTER_ALIAS,
  DEMO_DATE_FILTER_TITLE,
  DEMO_DATE_FORMAT,
  DEMO_ORDER_ITEM_BASE,
  DEMO_ORDER_ROWS,
  DEMO_PERIODS,
  DEMO_TOTAL_ORDERS,
  DEMO_TOTAL_STORE_ORDERS,
} from '../constants/pageAnalyticDemo';
import { calculatePercentageChange } from '../modules/analytic/helpers';
import { formatDateLabel } from '../modules/analytic/helpers/chart';
import { PREVIOUS_PERIOD_FILTER } from '~/presentation/ui';
import { ETimeDimension } from '~/core/gemxql/types';
import { convertDateToTz, dayjs } from '~/utils/dayjs';

export type IDemoPeriod = {
  startDate: Dayjs;
  endDate: Dayjs;
};

export type IDemoPeriods = {
  current: IDemoPeriod;
  previous: IDemoPeriod;
};

export type IDemoDateFilters = {
  date: DateTimeFilter;
  compareDate: DateTimeFilter;
};

type IGetDemoDayLabelParams = {
  startDate: Dayjs;
  dayIndex: number;
};

type IDemoDayLabel = {
  key: string;
  tooltipKey: string;
};

type IGetDemoLineChartDataParams = {
  key: string;
  allChartsDataMap: AllChartsDataMap;
  currentPeriodChartName: string;
  previousPeriodChartName: string;
};

/** The sample data's two periods, as days in the shop's timezone. */
export const getDemoPeriods = (): IDemoPeriods => {
  const { current, previous } = DEMO_PERIODS;

  return {
    current: {
      startDate: convertDateToTz(current.from),
      endDate: convertDateToTz(current.to).endOf('day'),
    },
    previous: {
      startDate: convertDateToTz(previous.from),
      endDate: convertDateToTz(previous.to).endOf('day'),
    },
  };
};

/**
 * The read-only date picker's ranges. Like the picker's own presets they are local days, so the
 * button reads the sample data's dates whatever the shop's timezone.
 */
export const getDemoDateFilters = (): IDemoDateFilters => {
  const { current, previous } = DEMO_PERIODS;

  return {
    date: {
      title: DEMO_DATE_FILTER_TITLE,
      alias: DEMO_DATE_FILTER_ALIAS,
      since: dayjs(current.from).valueOf(),
      until: dayjs(current.to).valueOf(),
    },
    compareDate: {
      ...PREVIOUS_PERIOD_FILTER,
      since: dayjs(previous.from).valueOf(),
      until: dayjs(previous.to).valueOf(),
    },
  };
};

const getDemoDayLabel = ({ startDate, dayIndex }: IGetDemoDayLabelParams): IDemoDayLabel =>
  formatDateLabel(startDate.add(dayIndex, 'day').format(DEMO_DATE_FORMAT), ETimeDimension.DAY);

export const buildDemoChartsDataMap = ({ current, previous }: IDemoPeriods): AllChartsDataMap => {
  const chartsDataMap: AllChartsDataMap = {};

  Object.entries(DEMO_DAILY_SERIES).forEach(([metricKey, series]) => {
    const currentPoints: IPAChartDataPoint[] = series.current.map((value, dayIndex) => {
      const previousValue = series.previous[dayIndex];
      return {
        ...getDemoDayLabel({ startDate: current.startDate, dayIndex }),
        value,
        trend: {
          value: calculatePercentageChange(value, previousValue),
          trend: value >= previousValue ? 'positive' : 'negative',
        },
      };
    });
    const previousPoints: IPAChartDataPoint[] = series.previous.map((value, dayIndex) => ({
      ...getDemoDayLabel({ startDate: previous.startDate, dayIndex }),
      value,
    }));

    chartsDataMap[metricKey] = { current: currentPoints, previous: previousPoints };
  });

  return chartsDataMap;
};

/** One metric's current and comparison series, as the SDK single-metric card draws them. */
export const getDemoLineChartData = ({
  key,
  allChartsDataMap,
  currentPeriodChartName,
  previousPeriodChartName,
}: IGetDemoLineChartDataParams): DataSeries[] => {
  const chartData = allChartsDataMap[key];
  if (!chartData) return [];

  return [
    { name: currentPeriodChartName, data: chartData.current },
    { name: previousPeriodChartName, data: chartData.previous, isComparison: true },
  ];
};

/** The sample orders in the shape the latest-orders table reads. */
export const buildDemoLatestOrders = (): IOrderListingLatestDataContext => {
  const orders = DEMO_ORDER_ROWS.map((row) => ({
    order_id: row.orderId,
    order_date: row.date,
    date: row.date,
    total_price: row.totalPrice,
    traffic_source: row.trafficSource,
    device: row.device,
    visitor_type: row.visitorType,
    experiments: [],
    items: row.items.map((item) => ({
      ...DEMO_ORDER_ITEM_BASE,
      product_title: item.productTitle,
      title: item.variantTitle,
      quantity: String(item.quantity),
    })),
    events: [],
  }));

  return {
    // Same cast as the builder: the SDK order type has fields the latest-orders table never reads.
    orderListingLatest: orders as unknown as IOrder[],
    totalFilteredOrders: DEMO_TOTAL_ORDERS,
    totalStoreOrders: DEMO_TOTAL_STORE_ORDERS,
    isLoadingLatest: false,
  };
};
